import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
//primereact
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { InputText } from "primereact/inputtext";
import { DataTable } from "primereact/datatable";
//components
import Panel from "../../../../components/panel/panel";
//services
import { getTemporaryUrl } from "../../../../services/File/getTemporaryUrl";
//context
import { useAppContext } from "../../../../context/AppContext";
import { deleteReportSped } from "../../../../services/Report/deleteReportSped";
import { confirmDialog } from "primereact/confirmdialog";

const TableSped = ({ data, onDeleteSuccess }) => {
  const { permissions } = useAppContext();
  const [search, setSearch] = useState("");
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);
  const [disableButtons, setDisableButtons] = useState(false);

  useEffect(() => {
    setSearch("");
  }, [data]);

  const groupedData = useMemo(() => {
    if (!Array.isArray(data)) return [];
    const tempMap = {};
    data.forEach((item) => {
      const regex = /periodo\s+(\d{1,2})\/(\d{4})/i;
      const match = item.name.match(regex);
      let month = null,
        year = null;
      if (match && match.length === 3) {
        month = match[1].padStart(2, "0");
        year = match[2];
      }
      if (!month || !year) return;
      const periodKey = `${month}/${year}`;
      if (!tempMap[periodKey]) {
        tempMap[periodKey] = {
          period: periodKey,
          created_at: item.created_at,
          pdfId: null,
          txtId: null,
        };
      }
      if (new Date(item.created_at) > new Date(tempMap[periodKey].created_at)) {
        tempMap[periodKey].created_at = item.created_at;
      }
      if (item.type_file === "sped_pdf") {
        if (tempMap[periodKey].pdfId) {
          const existingPdf = data.find(
            (d) => d.id === tempMap[periodKey].pdfId
          );
          if (
            existingPdf &&
            new Date(item.created_at) > new Date(existingPdf.created_at)
          ) {
            tempMap[periodKey].pdfId = item.id;
          }
        } else {
          tempMap[periodKey].pdfId = item.id;
        }
      }
      if (item.type_file === "sped_txt") {
        if (tempMap[periodKey].txtId) {
          const existingTxt = data.find(
            (d) => d.id === tempMap[periodKey].txtId
          );
          if (
            existingTxt &&
            new Date(item.created_at) > new Date(existingTxt.created_at)
          ) {
            tempMap[periodKey].txtId = item.id;
          }
        } else {
          tempMap[periodKey].txtId = item.id;
        }
      }
    });
    return Object.values(tempMap);
  }, [data]);

  const filteredData = useMemo(() => {
    if (!groupedData) return [];
    return groupedData.filter((row) => {
      if (!search) return true;
      const lower = search.toLowerCase();
      return row.period.toLowerCase().includes(lower);
    });
  }, [groupedData, search]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const d = new Date(dateStr);
    return d.toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleOpenPdf = async (pdfId) => {
    if (!pdfId) return;
    const notification = toast.loading("Abrindo PDF...");
    setDisableButtons(true);
    try {
      const tempUrl = await getTemporaryUrl(pdfId);
      if (tempUrl?.url) {
        window.open(tempUrl.url, "_blank");
        toast.update(notification, {
          render: "PDF aberto com sucesso!",
          type: "success",
          isLoading: false,
          autoClose: 2000,
        });
      } else {
        throw new Error("URL inválida");
      }
    } catch (err) {
      toast.update(notification, {
        render: "Erro ao obter URL temporária do PDF",
        type: "error",
        isLoading: false,
        autoClose: 3000,
      });
      console.error(err);
    } finally {
      setDisableButtons(false);
    }
  };

  const handleDownloadTxt = async (txtId) => {
    if (!txtId) return;
    const notification = toast.loading("Baixando TXT...");
    setDisableButtons(true);
    try {
      const tempUrl = await getTemporaryUrl(txtId);
      if (!tempUrl?.url) throw new Error("URL inválida");
      const resp = await fetch(tempUrl.url);
      if (!resp.ok) throw new Error("Erro ao buscar o TXT");
      const blob = await resp.blob();
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = `sped_${txtId}.txt`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
      toast.update(notification, {
        render: "TXT baixado com sucesso!",
        type: "success",
        isLoading: false,
        autoClose: 2000,
      });
    } catch (err) {
      toast.update(notification, {
        render: "Erro ao baixar o TXT",
        type: "error",
        isLoading: false,
        autoClose: 3000,
      });
      console.error(err);
    } finally {
      setDisableButtons(false);
    }
  };

  const handleDelete = (row) => {
    const ids = [];
    if (row.pdfId) ids.push(row.pdfId);
    if (row.txtId) ids.push(row.txtId);
    if (ids.length === 0) return;

    confirmDialog({
      message: `Deseja apagar todos os arquivos do período ${row.period}?`,
      header: "Confirmação",
      icon: "pi pi-exclamation-triangle",
      accept: async () => {
        const notification = toast.loading("Deletando SPED...");
        setDisableButtons(true);
        try {
          await deleteReportSped({ files_ids: ids });
          toast.update(notification, {
            render: "SPED deletado com sucesso!",
            type: "success",
            isLoading: false,
            autoClose: 2000,
          });
          if (onDeleteSuccess) {
            onDeleteSuccess(row.period);
          }
        } catch (err) {
          toast.update(notification, {
            render: "Erro ao deletar SPED",
            type: "error",
            isLoading: false,
            autoClose: 3000,
          });
          console.error(err);
        } finally {
          setDisableButtons(false);
        }
      },
    });
  };

  const actionBodyTemplate = (row) => (
    <div style={{ display: "flex", gap: "0.5rem", justifyContent: "center" }}>
      {row.pdfId && (
        <Button
          icon="fa-solid fa-file-pdf"
          className="p-button-sm"
          severity="secondary"
          style={{ borderRadius: 4 }}
          onClick={() => handleOpenPdf(row.pdfId)}
          tooltip="Abrir PDF"
          disabled={disableButtons}
          tooltipOptions={{ position: "top" }}
        />
      )}
      {row.txtId && (
        <Button
          icon="fa-solid fa-file"
          className="p-button-sm"
          severity="info"
          style={{ borderRadius: 4 }}
          onClick={() => handleDownloadTxt(row.txtId)}
          tooltip="Baixar TXT"
          disabled={disableButtons}
          tooltipOptions={{ position: "top" }}
        />
      )}
      {permissions.includes("262") && (
        <Button
          icon="pi pi-trash"
          className="p-button-sm p-button-danger"
          style={{ borderRadius: 4 }}
          onClick={() => handleDelete(row)}
          tooltip="Deletar SPED"
          disabled={disableButtons}
          tooltipOptions={{ position: "top" }}
        />
      )}
    </div>
  );

  if (!Array.isArray(data) || data.length === 0) {
    return (
      <Panel className="p-4 mt-4">
        <span className="text-secondary">Nenhum SPED disponível</span>
      </Panel>
    );
  }

  return (
    <Panel title="SPED gerados" className="p-4 mt-4">
      <div className="mb-3 d-flex justify-content-end">
        <IconField iconPosition="left">
          <InputIcon className="pi pi-search" />
          <InputText
            placeholder="Pesquisar período (MM/YYYY)"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ minWidth: 220 }}
            className="rounded-4 ps-5"
          />
        </IconField>
      </div>
      <DataTable
        value={filteredData}
        paginator
        rows={10}
        rowsPerPageOptions={[10, 20, 50]}
        sortField={sortField}
        sortOrder={sortOrder}
        tableStyle={{ minWidth: "0.8rem", fontSize: "0.8rem" }}
        stripedRows
        emptyMessage={
          <span className="text-secondary">Nenhum SPED encontrado</span>
        }
        onSort={(e) => {
          setSortField(e.sortField);
          setSortOrder(e.sortOrder);
        }}
      >
        <Column
          field="created_at"
          header="Data de Criação"
          sortable
          body={(row) => formatDate(row.created_at)}
        />
        <Column
          field="period"
          header="Período"
          sortable
          body={(row) => row.period}
          style={{ width: "8rem", textAlign: "center" }}
        />
        <Column
          body={actionBodyTemplate}
          header="Ações"
          style={{ width: "10rem", textAlign: "center" }}
        />
      </DataTable>
    </Panel>
  );
};

export default TableSped;
